// chatbot.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { SharedService } from '../shared/services/shared.service';

@Injectable()
export class ChatbotService {
  isOpen = false;
  messages: any[] = [];
  userInput = '';
  faqData: any[] = [];
  currentQuestionOptions: [] = [];
  isAwaitingSelection: boolean
  isTyping = false;
  sessionId: string = this.generateSessionId();

  // Voice State
  isVoiceInputEnabled = false;
  isVoiceOutputEnabled = false;
  private audioContext: AudioContext | null = null;
  private currentAudioSource: AudioBufferSourceNode | null = null;

  constructor(private http: HttpClient, public sharedService: SharedService) {}

  private generateSessionId(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  // TTS Playback
  async playTTS(text: string) {
    if (!this.isVoiceOutputEnabled) return;

    try {
      // Fetch audio from backend as array buffer
      const response = await this.http.post(`${this.sharedService.apiUrl}/chatbot/tts`, { text }, { responseType: 'arraybuffer' }).toPromise();
      
      if (!response) return;

      // Initialize AudioContext if needed
      if (!this.audioContext) {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        this.audioContext = new AudioContextClass();
      }

      // Stop currently playing audio
      if (this.currentAudioSource) {
        this.currentAudioSource.stop();
      }

      // Decode and play new audio
      const audioBuffer = await this.audioContext.decodeAudioData(response as ArrayBuffer);
      this.currentAudioSource = this.audioContext.createBufferSource();
      this.currentAudioSource.buffer = audioBuffer;
      this.currentAudioSource.connect(this.audioContext.destination);
      this.currentAudioSource.start(0);

    } catch (error) {
      console.error('TTS playback failed:', error);
    }
  }

  stopTTS() {
    if (this.currentAudioSource) {
      this.currentAudioSource.stop();
      this.currentAudioSource = null;
    }
  }

   setFAQData(data: any[]) {
    this.faqData = data;
   // console.log('Chatbot FAQ data updated:', this.faqData);
  }

  // Enhanced send message that uses backend first
  sendMessage(message: string) {
    // Force requests to the backend so the Python server can answer
    const localAnswer = null; // this.findLocalFAQAnswer(message);
    
    if (localAnswer) {
      // Return local answer without API call
      return of({
        success: true,
        userMessage: message,
        botResponse: localAnswer,
        timestamp: new Date(),
        source: 'local'
      });
    }

    const userName = this.sharedService.logged_in_user?.name_first 
      ? `${this.sharedService.logged_in_user.name_first} ${this.sharedService.logged_in_user.name_last}`.trim()
      : 'Anonymous';

    // If no local answer found, use the backend chatbot
    return this.http.post(`${this.sharedService.apiUrl}/chatbot/message`, {
      message: message,
      userId: this.sharedService.logged_in_user?.user_id,
      companyId: this.sharedService.company_info?.company_id,
      userName: userName,
      sessionId: this.sessionId
    });
  }

  // Method to search local FAQ data
  private findLocalFAQAnswer(userMessage: string): string | null {
    if (!this.faqData || this.faqData.length === 0) {
      return null;
    }

    const message = userMessage.toLowerCase();
    let bestMatch = null;
    let bestScore = 0;

    // Search through FAQ categories and questions
    this.faqData.forEach(category => {
      if (category.questions && category.questions.length > 0) {
        category.questions.forEach((item: any) => {
          const score = this.calculateMatchScore(message, item);
          if (score > bestScore && score > 0.3) { // Threshold for relevance
            bestScore = score;
            bestMatch = item.answer;
          }
        });
      }
    });

    return bestMatch;
  }

  // Simple text matching algorithm
  private calculateMatchScore(userMessage: string, faqItem: any): number {
    const question = faqItem.question.toLowerCase();
    const answer = faqItem.answer.toLowerCase();
    const tags = faqItem.tags ? faqItem.tags.join(' ').toLowerCase() : '';
    
    const searchText = `${question} ${answer} ${tags}`;
    const userWords = userMessage.split(' ').filter(word => word.length > 2);
    
    let matches = 0;
    userWords.forEach(word => {
      if (searchText.includes(word)) {
        matches++;
      }
    });

    return matches / userWords.length;
  }

  // Rest of your existing methods...
  getAllFAQs(): Observable<any> {
    return this.http.get(`${this.sharedService.apiUrl}/faqs`);
  }

  getChatHistory(userId: string) {
    return this.http.get(
      `${this.sharedService.apiUrl}/chatbot/history/${this.sharedService.logged_in_user.user_id}`
    );
  }

  getFAQs(): Observable<any> {
    console.warn('getting FAQS');
    const test = this.http.get(`${this.sharedService.apiUrl}/chatbot/faqs`)
    console.warn(test);
    return this.http.get(`${this.sharedService.apiUrl}/chatbot/faqs`);
  }

  addFAQ(faq: any): Observable<any> {
    return this.http.post(`${this.sharedService.apiUrl}/chatbot/faq`, faq);
  }

  openChatbot() {
    this.isOpen = true;
  }

  closeChatbot() {
    this.isOpen = false;
  }
  
  toggleChatbot(is_open: boolean) {
    this.isOpen = is_open;
  }
    resetSelectionState() {
    this.isAwaitingSelection = false;
    this.currentQuestionOptions = [];
  }
}
